import { KatalComponent, Keys } from '../../shared/base';
import { getFirstMatchingParent } from '../../shared/utils';

const TRIGGER_TOGGLE = 'toggle';
const TRIGGER_DISPLAY = 'display';
const TRIGGER_HIDE = 'hide';

/**
 * @component {kat-popover-trigger} KatalPopoverTrigger The wrapper component is used to mark some content as a trigger and/or anchor for an associated popover.
 * It is automatically connected to the popover if placed inside it. If not inside a popover it should be given a "for" attribute to associate to a popover.
 */
class KatalPopoverTrigger extends KatalComponent {
  static get observedAttributes() {
    return ['type', 'position', 'for'];
  }

  constructor() {
    super(KatalPopoverTrigger.observedAttributes);

    this._popoverFocused = false;
    this._persistPopover = false;
    this._lastPopover = null;
    this._lastControl = null;
    this._lastContent = null;
    this._wasKeyboardInit = false;
    this._popoverHovered = false;
    this._triggerHovered = false;

    this.onFocusPopover = this.onFocusPopover.bind(this);
    this.onBlurPopover = this.onBlurPopover.bind(this);
    this.onClickPopover = this.onClickPopover.bind(this);
    this.onKeyPressPopover = this.onKeyPressPopover.bind(this);
    this.onChildrenChanged = this.onChildrenChanged.bind(this);
    this.onHoverOutPopover = this.onHoverOutPopover.bind(this);
    this.onHoverPopover = this.onHoverPopover.bind(this);
    this.hoverOutBehaviour = this.hoverOutBehaviour.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'keydown',
      evt => {
        if (evt.keyCode === Keys.Enter || evt.keyCode === Keys.Space) {
          this._wasKeyboardInit = true;
        }
      },
      true
    );
    this.createTriggerHandle('click', 'click', TRIGGER_TOGGLE, evt => {
      const popover = this.getPopover();
      // Clicks can be triggered by keyboard events as well but we only want to do the below logic if it's a keyboard event.
      if (!popover || !this._wasKeyboardInit) return true;

      this._wasKeyboardInit = false;

      if (popover.katAriaBehavior === 'interactive') {
        const content = popover.getContent();
        if (!content) return true;

        setTimeout(() => {
          content.focus();
        }, 0);
      }
      return true;
    });
    this.createTriggerHandle('focus', 'focusin', TRIGGER_DISPLAY, () => {
      this._popoverFocused = true;
      this._persistPopover = false;
      return true;
    });

    this.createTriggerHandle('focus', 'focusout', TRIGGER_HIDE, () => {
      const popover = this.getPopover();
      return popover.visible && !this._popoverFocused && !this._persistPopover;
    });

    this.createTriggerHandle(
      'hover',
      ['mouseenter', 'focusin'],
      TRIGGER_DISPLAY,
      () => {
        this._persistPopover = false;
        this._triggerHovered = true;
        return true;
      }
    );

    this.createTriggerHandle('hover', 'click', TRIGGER_TOGGLE, () => {
      const popover = this.getPopover();
      if (popover.visible && !this._persistPopover) {
        this.onClickPopover();
        return false;
      }

      return true;
    });

    // createTriggeHandle does not support async callbacks.
    this.addEventListener('mouseleave', () => {
      this._triggerHovered = false;
      this.hoverOutBehaviour();
    });
    this.addEventListener('focusout', () => {
      this._triggerHovered = false;
      this.hoverOutBehaviour();
    });

    this._observer = new MutationObserver(this.onChildrenChanged);
    this._observer.observe(this, { childList: true });

    const popover = this.getPopover();
    if (popover && popover.getContent) {
      this.render();
    } else {
      this.tryAgainForParentToBeConnected();
    }
  }

  disconnectedCallback() {
    this._observer.disconnect();
  }

  /**
   * Creates an event handle used to display or hide the associated popover.
   * @param {string} type Should be one of the possible values of the type property.
   * @param {string|Array} eventName Should be a standard event that can bubble up (e.g. "focus" and "blur" don't bubble).
   * @param {string} behaviour One of "toggle", "display", or "hide" that determine how this event affects the popover.
   *  - "toggle" means that each time the event fires the popover can transition from visible to not or vice-versa.
   *  - "display" means to be just displayed if not visible.
   *  - "hide" means it will be hidden if visible.
   * @param {function} behaviourHandlingCb This callback is invoked before the desired behaviour is attempted. A truthy value
   * will continue with desired behaviour and falsey value will hault the execution of desired behaviour.
   * @katalignore
   */
  createTriggerHandle(type, eventName, behavior, behaviourHandlingCb) {
    const events = Array.isArray(eventName) ? eventName : [eventName];

    events.forEach(event => {
      this.addEventListener(event, evt => {
        if (this.type !== type) return;

        const popover = this.getPopover();
        if (!popover) return;

        // At runtime, we allow the registrant to opt-out of the desired behaviour.
        if (behaviourHandlingCb && !behaviourHandlingCb(evt)) {
          return;
        }

        // If lastTrigger is this trigger then it's okay to hide otherwise this popover is being shared so it should be moved to this trigger
        if (popover.visible && popover.lastTrigger === this) {
          if (behavior === TRIGGER_HIDE || behavior === TRIGGER_TOGGLE) {
            popover.hide();
            this.updateAriaWhenPopoverToggled();
          }
        } else {
          if (behavior === TRIGGER_DISPLAY || behavior === TRIGGER_TOGGLE) {
            this.displayPopover(popover);
          }
        }
      });
    });
  }

  /**
   * @classprop {enum} type Determines how a user triggers the associated popover to show and hide when interacting with this trigger.
   * Defaults to click.
   * @enum {value} click When clicked and the popover is hidden the popover will be shown and vice-versa.
   * @enum {value} focus When an element inside the trigger is focused the popover appears. If the element is blurred the popover closes.
   */
  get type() {
    return this.getAttributeOrDefault('type', 'click');
  }
  set type(value) {
    this.setAttribute('type', value);
  }

  /**
   * @classprop {enum} position Determines where the popover will be anchored to this trigger. The popover will anchor the side specified and centered
   * Defaults to "bottom". If the popover would be clipped by the window at the specified position it will automatically move to another adjacent
   * position that isn't clipped.
   * @enum {value} top Positions the popover above the trigger.
   * @enum {value} right Positions the popover to the right of the trigger.
   * @enum {value} bottom Positions the popover below the trigger.
   * @enum {value} left Positions the popover to the left of the trigger.
   */
  get position() {
    return this.getAttributeOrDefault('position', 'bottom');
  }
  set position(value) {
    this.setAttribute('position', value);
  }

  /**
   * @classprop {string} for Specifies the ID of some popover on the page for this trigger to be associated with. Can be omitted if the <kat-popover-trigger>
   * is nested inside a <kat-popover> element.
   */
  get for() {
    return this.getAttribute('for');
  }
  set for(value) {
    this.setAttribute('for', value);
  }

  /**
   * Returns the popover associated with this trigger. Either finds the popover with the ID specified by the for attribute or selects the closest parent.
   */
  getPopover() {
    if (this.for) {
      const popover = document.getElementById(this.for);
      if (popover && popover.tagName === 'KAT-POPOVER') {
        return popover;
      }
    }

    return getFirstMatchingParent(this, ele => ele.tagName === 'KAT-POPOVER');
  }

  displayPopover(popover) {
    popover.displayTriggeredBy(this);

    // Attempt to hide all other popovers
    Array.from(document.getElementsByTagName('kat-popover')).forEach(other => {
      if (other !== popover) {
        other.hide();
      }
    });

    this.updateAriaWhenPopoverToggled();

    if (this._lastPopover === popover) return;

    // Remove events from last popover, necessary since events from that popover could cause undefined behaviour
    const lastContent = this._lastPopover && this._lastPopover.getContent();
    if (lastContent) {
      lastContent.removeEventListener('focusin', this.onFocusPopover);
      lastContent.removeEventListener('focusout', this.onBlurPopover);
      lastContent.removeEventListener('mousedown', this.onClickPopover);
      lastContent.removeEventListener('keyup', this.onKeyPressPopover);
      lastContent.removeEventListener('mouseenter', this.onHoverPopover);
      lastContent.removeEventListener('mouseleave', this.onHoverOutPopover);
    }

    // Register this popover as the current popover and add events.
    this._lastPopover = popover;

    const content = popover.getContent();
    if (content) {
      content.addEventListener('focusin', this.onFocusPopover);
      content.addEventListener('focusout', this.onBlurPopover);
      content.addEventListener('mousedown', this.onClickPopover);
      content.addEventListener('keyup', this.onKeyPressPopover);
      content.addEventListener('mouseenter', this.onHoverPopover);
      content.addEventListener('mouseleave', this.onHoverOutPopover);
    }
  }

  onHoverPopover() {
    this._popoverHovered = true;
  }

  onHoverOutPopover() {
    this._popoverHovered = false;
    this.hoverOutBehaviour();
  }

  hoverOutBehaviour() {
    if (this.type !== 'hover') return;

    if (!this._persistPopover) {
      setTimeout(() => {
        const popover = this.getPopover();
        if (
          popover &&
          popover.visible &&
          !this._popoverHovered &&
          !this._triggerHovered
        ) {
          this._persistPopover = false;
          popover.hide();
          this.updateAriaWhenPopoverToggled();
        }
      }, 200);
    }
  }

  hideIfNotFocused() {
    if (this.type !== 'focus') return;

    this._popoverFocused = false;
    setTimeout(() => {
      if (!this._popoverFocused && !this._persistPopover) {
        const popover = this.getPopover();
        if (popover && popover.visible) {
          popover.hide();
          this.updateAriaWhenPopoverToggled();
        }
      }
    }, 0);
  }

  onFocusPopover() {
    this._popoverFocused = true;
  }

  onBlurPopover() {
    this.hideIfNotFocused();
  }

  onClickPopover() {
    this._persistPopover = true;
  }

  onKeyPressPopover(evt) {
    if (evt.keyCode === Keys.Escape) {
      const popover = this.getPopover();
      if (popover) {
        popover.hide();
        this.updateAriaWhenPopoverToggled();
      }
    }
  }

  onChildrenChanged() {
    this.render();
  }

  /**
   * Used to update the aria attributes when the related popover changes its aria behavior.
   */
  updateAriaAttributes() {
    this.render();
  }

  updateAriaWhenPopoverToggled() {
    const popover = this.getPopover();
    const target = this.children[0];
    if (!popover || !target) return;

    if (popover.katAriaBehavior === 'interactive') {
      target.setAttribute('aria-expanded', popover.visible);
    }
  }

  render() {
    const popover = this.getPopover();
    const content = popover && popover.getContent && popover.getContent();
    const control = this.children[0];
    if (!control || !content) {
      return;
    }

    // Do nothing if the aria relevant elements haven't changed
    if (
      this._lastControl === control &&
      this._lastContent === content &&
      this._lastBehavior === popover.katAriaBehavior
    ) {
      return;
    }

    // If the control changed remove the associated ids
    if (
      (this._lastControl !== control ||
        this._lastBehavior !== popover.katAriaBehavior) &&
      this._lastControl
    ) {
      this._lastControl.removeAttribute('aria-controls');
      this._lastControl.removeAttribute('aria-expanded');
      this._lastControl.removeAttribute('aria-flowto');
      this._lastControl.removeAttribute('aria-describedby');
    }

    this._lastBehavior = popover.katAriaBehavior;
    this._lastControl = control;
    this._lastContent = content;

    // Best practices as laid out by this site: https://baseweb.design/components/popover/#popover-opens-on-hover
    const popoverId = content.getAttribute('id');
    if (popover.katAriaBehavior === 'interactive') {
      // Interactive treats the popover like a panel with interactable elements
      control.setAttribute('aria-controls', popoverId);
      control.setAttribute('aria-expanded', popover.visible);

      // When the popover is distant use aria-flowto to allow the user to move to the popover content
      if (this.for) {
        control.setAttribute('aria-flowto', popoverId);
      }
    } else if (popover.katAriaBehavior === 'tooltip') {
      // Tooltip treats the popover as a tooltip with simple content
      control.setAttribute('aria-describedby', popoverId);
    }
  }

  tryAgainForParentToBeConnected() {
    window.requestAnimationFrame(() => {
      this.render();
    });
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (attr === 'for') {
        this.render();
      }
    }
  }
}

KatalComponent.register('kat-popover-trigger', KatalPopoverTrigger);
